import firebase from "firebase/compat/app";
import "firebase/compat/auth";

let cachedAccessToken: string | null = null;

const JWT_EXPIRY_SKEW_MS = 60_000;
const TOKEN_RETRY_DELAY_MS = 400;
const AUTH_BRIDGE_INIT_MS = 5_000;

type AccessTokenListener = (token: string | null) => void;

const accessTokenListeners = new Set<AccessTokenListener>();

let inFlightTokenRequest: Promise<string | null> | null = null;

let authBridgeInstalled = false;
let authBridgeInitialized = false;

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split(".");
  if (parts.length < 2) {
    return null;
  }
  try {
    const normalized = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      "="
    );
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function isJwtExpiredOrNearExpiry(
  token: string,
  skewMs = JWT_EXPIRY_SKEW_MS
): boolean {
  const payload = decodeJwtPayload(token);
  const exp = payload?.exp;
  if (typeof exp !== "number") {
    return true;
  }
  return Date.now() >= exp * 1000 - skewMs;
}

export function getCachedAccessToken(): string | null {
  return cachedAccessToken;
}

export function setCachedAccessToken(token: string | null) {
  cachedAccessToken = token;
  accessTokenListeners.forEach(listener => {
    listener(token);
  });
}

export function subscribeAccessToken(
  listener: AccessTokenListener
): () => void {
  accessTokenListeners.add(listener);
  if (isUsableServiceAccessToken(cachedAccessToken)) {
    listener(cachedAccessToken);
  }
  return () => {
    accessTokenListeners.delete(listener);
  };
}

export function isAuthBridgeInitialized(): boolean {
  return authBridgeInitialized;
}

async function fetchIdTokenFromFirebase(
  user: firebase.User,
  forceRefresh: boolean
): Promise<string> {
  if (inFlightTokenRequest) {
    return inFlightTokenRequest;
  }

  inFlightTokenRequest = (async () => {
    try {
      let token = await user.getIdToken(forceRefresh);
      if (
        !forceRefresh &&
        isUsableFirebaseIdTokenParam(token) &&
        isJwtExpiredOrNearExpiry(token)
      ) {
        token = await user.getIdToken(true);
      }
      return token;
    } finally {
      inFlightTokenRequest = null;
    }
  })();

  return inFlightTokenRequest;
}

export async function ensureAccessToken(options?: {
  forceRefresh?: boolean;
}): Promise<string | null> {
  const user = firebase.auth().currentUser;
  if (!user) {
    return null;
  }

  const forceRefresh = Boolean(options?.forceRefresh);

  if (!forceRefresh && isUsableServiceAccessToken(cachedAccessToken)) {
    return cachedAccessToken;
  }

  const token = await fetchIdTokenFromFirebase(user, forceRefresh);
  setCachedAccessToken(token);
  return token;
}

export async function ensureAccessTokenWithRetry(options?: {
  forceRefresh?: boolean;
  retries?: number;
}): Promise<string | null> {
  const retries = options?.retries ?? 1;
  let lastError: unknown;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      const forceRefresh = Boolean(options?.forceRefresh) && attempt === 0;
      return await ensureAccessToken({ forceRefresh });
    } catch (err) {
      lastError = err;
      if (attempt < retries) {
        await new Promise<void>(resolve => {
          if (!process.env.BROWSER) {
            resolve();
            return;
          }
          window.setTimeout(resolve, TOKEN_RETRY_DELAY_MS);
        });
      }
    }
  }
  throw lastError;
}

/** Cache ID token immediately after `firebase.login` so auth shell can render without an extra wait. */
export async function seedSessionAccessToken(): Promise<string | null> {
  return ensureAccessToken({ forceRefresh: false });
}

export function isUsableServiceAccessToken(
  value: string | null | undefined
): boolean {
  if (!isUsableFirebaseIdTokenParam(value)) {
    return false;
  }
  return !isJwtExpiredOrNearExpiry((value as string).trim());
}

export function resolveServiceAccessTokenFromState(
  reduxStsToken: string | null | undefined
): string | undefined {
  if (!firebase.auth().currentUser) {
    return undefined;
  }
  if (isUsableServiceAccessToken(cachedAccessToken)) {
    return (cachedAccessToken as string).trim();
  }
  if (isUsableServiceAccessToken(reduxStsToken)) {
    const t = (reduxStsToken as string).trim();
    cachedAccessToken = t;
    return t;
  }
  return undefined;
}

export function clearSessionAccessToken(): void {
  cachedAccessToken = null;
  accessTokenListeners.forEach(listener => listener(null));
}

/** Rejects values that become `token=undefined` / `token=null` in query strings. */
export function isUsableFirebaseIdTokenParam(
  value: string | null | undefined
): boolean {
  if (value == null || typeof value !== "string") return false;
  const t = value.trim();
  if (t.length === 0) return false;
  if (t === "undefined" || t === "null") return false;
  return true;
}

/**
 * Resolves a Firebase ID token for API calls using Authorization Bearer headers.
 * When a Firebase session exists, always reads from the SDK (honours expiry/cache).
 */
export async function resolveFirebaseIdTokenForApiCall(
  token: string | null | undefined
): Promise<string> {
  const user = firebase.auth().currentUser;
  if (user) {
    const refreshed = await ensureAccessToken({ forceRefresh: false });
    if (isUsableServiceAccessToken(refreshed)) {
      return (refreshed as string).trim();
    }
  }
  if (isUsableServiceAccessToken(token)) {
    return (token as string).trim();
  }
  throw new Error("Missing or invalid Firebase ID token");
}

function syncAccessTokenFromFirebase(forceRefresh = false): void {
  ensureAccessToken({ forceRefresh })
    .then(token => {
      if (token) {
        setCachedAccessToken(token);
      }
    })
    .catch(() => {
      /* withServiceAccessToken stuck watch / retry handles recovery */
    });
}

export function installServiceAccessTokenBridge(): void {
  if (authBridgeInstalled || !process.env.BROWSER) {
    return;
  }
  authBridgeInstalled = true;

  const authReadyFallbackId = window.setTimeout(() => {
    authBridgeInitialized = true;
  }, AUTH_BRIDGE_INIT_MS);

  firebase.auth().onAuthStateChanged(user => {
    authBridgeInitialized = true;
    window.clearTimeout(authReadyFallbackId);
    if (!user) {
      clearSessionAccessToken();
      return;
    }
    syncAccessTokenFromFirebase(false);
  });

  firebase.auth().onIdTokenChanged(user => {
    if (!user) {
      clearSessionAccessToken();
      return;
    }
    syncAccessTokenFromFirebase(false);
  });

  const refreshOnFocus = () => {
    if (!firebase.auth().currentUser) {
      return;
    }
    syncAccessTokenFromFirebase(false);
  };

  window.addEventListener("focus", refreshOnFocus);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      refreshOnFocus();
    }
  });
}

/** @deprecated use installServiceAccessTokenBridge */
export function bootstrapAuthAccessTokenCache(): () => void {
  installServiceAccessTokenBridge();
  return () => {};
}

if (__DEV__) {
  if (!(isUsableServiceAccessToken(null) === false)) {
    console.error("firebaseToken: null is not usable");
  }
}
